import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";

export const LevelAdmin = () => {
  const [cfg, setCfg] = useState(null);
  const [dist, setDist] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [grant, setGrant] = useState({ player_number: "", amount: "" });
  const [setLvl, setSetLvl] = useState({ player_number: "", level: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    gameApi.get("/game/admin/levels/config").then((r) => setCfg(r.data)).catch(() => {});
    gameApi.get("/game/admin/levels/distribution").then((r) => setDist(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const loadPreview = async () => {
    if (!preview) { const { data } = await gameApi.get("/game/admin/levels/preview"); setPreview(data.rows); }
    setShowPreview((v) => !v);
  };

  const saveCfg = async () => {
    setBusy(true);
    try { const { data } = await gameApi.put("/game/admin/levels/config", cfg); setCfg(data); toast.success("Level config saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const doGrant = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/admin/levels/grant-xp", { player_number: parseInt(grant.player_number), amount: parseInt(grant.amount) });
      toast.success(data.awarded ? `Granted ${grant.amount} XP → Level ${data.new_level}` : "No change");
      setGrant({ player_number: "", amount: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const doSetLevel = async () => {
    setBusy(true);
    try {
      const { data } = await gameApi.post("/game/admin/levels/set-level", { player_number: parseInt(setLvl.player_number), level: parseInt(setLvl.level) });
      toast.success(`Set player #${setLvl.player_number} to Level ${data.level.level}`);
      setSetLvl({ player_number: "", level: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!cfg) return <p className="p-4 text-sm text-muted-foreground">Loading level system…</p>;
  const num = (path, val) => {
    const c = JSON.parse(JSON.stringify(cfg));
    const keys = path.split("."); let o = c;
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
    o[keys[keys.length - 1]] = val === "" ? "" : Number(val);
    setCfg(c);
  };
  const field = (label, path, value) => (
    <label key={path} className="text-xs text-muted-foreground">{label}
      <input data-testid={`lvl-cfg-${path}`} type="number" value={value} onChange={(e) => num(path, e.target.value)}
        className="mt-1 h-8 w-full rounded-lg border border-border bg-background px-2 text-sm" /></label>
  );

  return (
    <div className="space-y-4" data-testid="level-admin">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm font-bold text-primary">
          <input data-testid="lvl-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
          XP System Enabled
        </label>
      </div>

      <div className="rounded-xl border border-border bg-card p-3">
        <p className="mb-2 text-sm font-bold text-primary">XP Curve & Currency</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {field("Curve base", "curve.base", cfg.curve.base)}
          {field("Curve per-level", "curve.per_level", cfg.curve.per_level)}
          {field("Curve exp coeff", "curve.exp_coeff", cfg.curve.exp_coeff)}
          {field("Currency base", "currency.base", cfg.currency.base)}
          {field("Currency per-level", "currency.per_level", cfg.currency.per_level)}
          {field("Rest XP", "rest_xp", cfg.rest_xp)}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-3">
        <p className="mb-2 text-sm font-bold text-primary">Care XP</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {field("Hay", "care.hay", cfg.care.hay)}
          {field("Water", "care.water", cfg.care.water)}
          {field("Groom", "care.groom", cfg.care.groom)}
          {field("Health check", "care.health_check", cfg.care.health_check)}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-3">
        <p className="mb-2 text-sm font-bold text-primary">Competition XP</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {field("1st", "competition.1", cfg.competition["1"])}
          {field("2nd", "competition.2", cfg.competition["2"])}
          {field("3rd", "competition.3", cfg.competition["3"])}
          {field("4th–6th", "competition.mid", cfg.competition.mid)}
          {field("7th–9th", "competition.low", cfg.competition.low)}
          {field("Participation", "competition.participation", cfg.competition.participation)}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-3">
        <p className="mb-2 text-sm font-bold text-primary">Bonus XP Sources</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {field("Makeover complete", "makeover_complete_xp", cfg.makeover_complete_xp)}
          {field("Daily goal XP", "daily_goal_xp", cfg.daily_goal_xp)}
          {field("Weekly goal XP", "weekly_goal_xp", cfg.weekly_goal_xp)}
        </div>
        <div className="mt-2 flex gap-4">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input data-testid="lvl-daily-en" type="checkbox" checked={!!cfg.daily_goal_enabled} onChange={(e) => setCfg({ ...cfg, daily_goal_enabled: e.target.checked })} /> Daily goal enabled</label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input data-testid="lvl-weekly-en" type="checkbox" checked={!!cfg.weekly_goal_enabled} onChange={(e) => setCfg({ ...cfg, weekly_goal_enabled: e.target.checked })} /> Weekly goal enabled</label>
        </div>
      </div>

      <button data-testid="lvl-cfg-save" onClick={saveCfg} disabled={busy} className="h-10 w-full rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60">Save Level Config</button>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="mb-2 text-sm font-bold text-primary">Grant XP</p>
          <div className="flex gap-2">
            <input data-testid="lvl-grant-num" placeholder="Player #" value={grant.player_number} onChange={(e) => setGrant({ ...grant, player_number: e.target.value })} className="h-9 w-24 rounded-lg border border-border bg-background px-2 text-sm" />
            <input data-testid="lvl-grant-amt" placeholder="XP" value={grant.amount} onChange={(e) => setGrant({ ...grant, amount: e.target.value })} className="h-9 flex-1 rounded-lg border border-border bg-background px-2 text-sm" />
            <button data-testid="lvl-grant-btn" onClick={doGrant} disabled={busy} className="h-9 rounded-lg bg-secondary px-3 text-xs font-bold text-white">Grant</button>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="mb-2 text-sm font-bold text-primary">Set Exact Level</p>
          <div className="flex gap-2">
            <input data-testid="lvl-set-num" placeholder="Player #" value={setLvl.player_number} onChange={(e) => setSetLvl({ ...setLvl, player_number: e.target.value })} className="h-9 w-24 rounded-lg border border-border bg-background px-2 text-sm" />
            <input data-testid="lvl-set-lvl" placeholder="Level 1–500" value={setLvl.level} onChange={(e) => setSetLvl({ ...setLvl, level: e.target.value })} className="h-9 flex-1 rounded-lg border border-border bg-background px-2 text-sm" />
            <button data-testid="lvl-set-btn" onClick={doSetLevel} disabled={busy} className="h-9 rounded-lg bg-secondary px-3 text-xs font-bold text-white">Set</button>
          </div>
        </div>
      </div>

      <button data-testid="lvl-preview-toggle" onClick={loadPreview} className="h-9 w-full rounded-lg bg-muted text-xs font-bold text-primary">{showPreview ? "Hide" : "Show"} XP Preview (1–500)</button>
      {showPreview && preview && (
        <div className="max-h-72 overflow-auto rounded-xl border border-border" data-testid="lvl-preview-table">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-card"><tr className="text-left text-muted-foreground"><th className="p-1">Lvl</th><th className="p-1">XP to next</th><th className="p-1">Cumulative</th><th className="p-1">Coins</th><th className="p-1">Reward</th></tr></thead>
            <tbody>
              {preview.map((r) => (
                <tr key={r.level} className={r.milestone ? "bg-emerald-50 font-bold" : ""}>
                  <td className="p-1">{r.level}</td><td className="p-1">{r.xp_required.toLocaleString()}</td><td className="p-1">{r.cumulative_xp.toLocaleString()}</td><td className="p-1">{r.currency_reward}🪙</td><td className="p-1">{r.milestone ? "🐴 Coat" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dist && (
        <div className="rounded-xl border border-border bg-card p-3" data-testid="lvl-distribution">
          <p className="mb-2 text-sm font-bold text-primary">Players per Level ({dist.total_players} total)</p>
          <div className="flex flex-wrap gap-1.5">
            {dist.distribution.map((d) => <span key={d.level} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">L{d.level}: {d.players}</span>)}
          </div>
          <p className="mb-1 mt-3 text-sm font-bold text-primary">Reward Coats Unlocked</p>
          <div className="flex flex-wrap gap-1.5">
            {dist.rewards.map((r) => <span key={r.level} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">L{r.level}: {r.unlocked_by}</span>)}
          </div>
        </div>
      )}
    </div>
  );
};
